import { cx } from "../../lib/cx.js";
import { Button } from "../Button/Button.js";
import { focusFirst } from "../_overlay/focus.js";
import { LinkButton } from "../LinkButton/LinkButton.js";
import { Alert } from "../Alert/Alert.js";
import { useControllable } from "../../lib/useControllable.js";
import { Card, CardBody, CardFooter, CardHeader } from "../Card/Card.js";
import { Input } from "../Input/Input.js";
import { Modal } from "../Modal/Modal.js";
/* empty css                    */
import { useId, useRef, useState } from "react";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
//#region src/components/SettingsSection/SettingsSection.tsx
/**
* A Settings panel: read-only (a DescriptionList), an editable form (Edit, then Save or
* Cancel), or a managed list. Settings is read-only for now (#20), but sections can
* become editable one at a time. The design is proposed.
*/
function SettingsSection({ title, description, hint, mode = "readonly", children, form, editing: editingProp, onEditingChange, onSave, onCancel, dirty = false, saving: savingProp, error, editLabel = "Edit", className }) {
	const id = useId();
	const [editing, setEditing] = useControllable(editingProp, false, onEditingChange);
	const [pending, setPending] = useState(false);
	const [failure, setFailure] = useState(null);
	const [status, setStatus] = useState("");
	const [confirmingDiscard, setConfirmingDiscard] = useState(false);
	const editRef = useRef(null);
	const formRef = useRef(null);
	const saving = savingProp ?? pending;
	const focusLater = (target) => requestAnimationFrame(() => target()?.focus());
	const startEditing = () => {
		setStatus("");
		setFailure(null);
		setEditing(true);
		requestAnimationFrame(() => {
			if (formRef.current) focusFirst(formRef.current);
		});
	};
	const stopEditing = (message) => {
		setEditing(false);
		setFailure(null);
		setStatus(message);
		focusLater(() => editRef.current);
	};
	const save = async (event) => {
		event.preventDefault();
		setFailure(null);
		setPending(true);
		try {
			await onSave?.();
			stopEditing("Saved");
		} catch (caught) {
			setFailure(caught instanceof Error ? caught.message : "Your changes couldn’t be saved.");
		} finally {
			setPending(false);
		}
	};
	const cancel = () => {
		if (dirty) {
			setConfirmingDiscard(true);
			return;
		}
		onCancel?.();
		stopEditing("");
	};
	const shownError = error ?? failure;
	return /* @__PURE__ */ jsxs(Card, {
		className: cx("ds-settings-section", className),
		children: [
			/* @__PURE__ */ jsx(CardHeader, {
				id: `${id}title`,
				title: hint ? /* @__PURE__ */ jsxs("span", {
					className: "ds-settings-section__heading",
					children: [/* @__PURE__ */ jsx("span", { children: title }), /* @__PURE__ */ jsx("span", {
						className: "ds-settings-section__hint",
						children: hint
					})]
				}) : title,
				subtitle: description,
				actions: mode === "form" && !editing ? /* @__PURE__ */ jsx(Button, {
					ref: editRef,
					variant: "secondary",
					size: "sm",
					onClick: startEditing,
					children: editLabel
				}) : void 0
			}),
			mode === "form" && editing ? /* @__PURE__ */ jsxs("form", {
				ref: formRef,
				onSubmit: (event) => void save(event),
				noValidate: true,
				children: [/* @__PURE__ */ jsxs(CardBody, {
					className: "ds-settings-section__form",
					children: [form, shownError && /* @__PURE__ */ jsx(Alert, {
						tone: "danger",
						title: "Not saved",
						children: shownError
					})]
				}), /* @__PURE__ */ jsxs(CardFooter, { children: [/* @__PURE__ */ jsx(Button, {
					variant: "secondary",
					onClick: cancel,
					disabled: saving,
					children: "Cancel"
				}), /* @__PURE__ */ jsx(Button, {
					type: "submit",
					loading: saving,
					loadingText: "Saving…",
					children: "Save"
				})] })]
			}) : /* @__PURE__ */ jsx(CardBody, { children }),
			/* @__PURE__ */ jsx("span", {
				className: "ds-settings-section__status",
				role: "status",
				children: status
			}),
			/* @__PURE__ */ jsx(Modal, {
				open: confirmingDiscard,
				onClose: () => setConfirmingDiscard(false),
				title: "Discard your changes?",
				size: "sm",
				footer: /* @__PURE__ */ jsxs(Fragment$1, { children: [/* @__PURE__ */ jsx(Button, {
					variant: "secondary",
					onClick: () => setConfirmingDiscard(false),
					children: "Keep editing"
				}), /* @__PURE__ */ jsx(Button, {
					variant: "danger",
					onClick: () => {
						setConfirmingDiscard(false);
						onCancel?.();
						stopEditing("Changes discarded");
					},
					children: "Discard"
				})] }),
				children: "What you changed in this section won’t be saved."
			})
		]
	});
}
/**
* Runs a handler; false when it rejected. The caller reports the failure (a Toast), so
* the list only keeps what was entered and announces nothing.
*/
async function succeeded(handler) {
	try {
		await handler();
		return true;
	} catch {
		return false;
	}
}
/**
* A managed list for a Settings section (External ID types): each item with Rename (when
* `onRename` is given) and Delete, and a field to add one. A handler that rejects leaves
* things as they were (the name typed, the rename open, the delete dialog up): the caller
* says why, usually in a Toast. Rename edits in place; Delete confirms in a Modal and
* warns when the item is in use. The design is proposed.
*/
function SettingsList({ items, itemLabel, usageLabel = ["record", "records"], onAdd, onRename, onDelete, emptyLabel = "None yet" }) {
	const [adding, setAdding] = useState("");
	const [renaming, setRenaming] = useState(null);
	const [deleting, setDeleting] = useState(null);
	const [status, setStatus] = useState("");
	const listRef = useRef(null);
	const capitalised = itemLabel.charAt(0).toUpperCase() + itemLabel.slice(1);
	const [usageOne, usageMany] = typeof usageLabel === "string" ? [usageLabel, usageLabel] : usageLabel;
	const usageWord = (count) => count === 1 ? usageOne : usageMany;
	const add = async (event) => {
		event.preventDefault();
		const name = adding.trim();
		if (!name) return;
		if (!await succeeded(() => onAdd(name))) return;
		setAdding("");
		setStatus(`${name} added`);
	};
	const rename = async (event) => {
		event.preventDefault();
		if (!renaming || !onRename) return;
		const name = renaming.name.trim();
		if (name && !await succeeded(() => onRename(renaming.id, name))) return;
		const id = renaming.id;
		setRenaming(null);
		setStatus(name ? `Renamed to ${name}` : "");
		requestAnimationFrame(() => listRef.current?.querySelector(`[data-rename="${CSS.escape(id)}"]`)?.focus());
	};
	const confirmDelete = async () => {
		if (!deleting) return;
		if (!await succeeded(() => onDelete(deleting.id))) return;
		setStatus(`${deleting.name} deleted`);
		setDeleting(null);
	};
	return /* @__PURE__ */ jsxs("div", {
		className: "ds-settings-list",
		children: [
			items.length === 0 ? /* @__PURE__ */ jsx("p", {
				className: "ds-settings-list__empty",
				children: emptyLabel
			}) : /* @__PURE__ */ jsx("ul", {
				ref: listRef,
				className: "ds-settings-list__items",
				children: items.map((item) => renaming?.id === item.id ? /* @__PURE__ */ jsx("li", {
					className: "ds-settings-list__item",
					children: /* @__PURE__ */ jsxs("form", {
						className: "ds-settings-list__rename",
						onSubmit: (event) => void rename(event),
						children: [
							/* @__PURE__ */ jsx(Input, {
								size: "sm",
								"aria-label": `Rename ${item.name}`,
								value: renaming.name,
								autoFocus: true,
								onChange: (event) => setRenaming({
									id: item.id,
									name: event.target.value
								}),
								onKeyDown: (event) => {
									if (event.key === "Escape") {
										event.preventDefault();
										setRenaming(null);
									}
								}
							}),
							/* @__PURE__ */ jsx(Button, {
								size: "sm",
								type: "submit",
								children: "Save"
							}),
							/* @__PURE__ */ jsx(Button, {
								size: "sm",
								variant: "secondary",
								onClick: () => setRenaming(null),
								children: "Cancel"
							})
						]
					})
				}, item.id) : /* @__PURE__ */ jsxs("li", {
					className: "ds-settings-list__item",
					children: [
						/* @__PURE__ */ jsx("span", {
							className: "ds-settings-list__name",
							children: item.name
						}),
						item.usage !== void 0 && item.usage > 0 && /* @__PURE__ */ jsxs("span", {
							className: "ds-settings-list__usage",
							children: [
								"Used by ",
								item.usage,
								" ",
								usageWord(item.usage)
							]
						}),
						/* @__PURE__ */ jsxs("span", {
							className: "ds-settings-list__actions",
							children: [onRename && /* @__PURE__ */ jsx(LinkButton, {
								tone: "neutral",
								"data-rename": item.id,
								"aria-label": `Rename ${item.name}`,
								onClick: () => setRenaming({
									id: item.id,
									name: item.name
								}),
								children: "Rename"
							}), /* @__PURE__ */ jsx(LinkButton, {
								tone: "neutral",
								"aria-label": `Delete ${item.name}`,
								onClick: () => setDeleting(item),
								children: "Delete"
							})]
						})
					]
				}, item.id))
			}),
			/* @__PURE__ */ jsxs("form", {
				className: "ds-settings-list__add",
				onSubmit: (event) => void add(event),
				children: [/* @__PURE__ */ jsx(Input, {
					size: "sm",
					"aria-label": `New ${itemLabel}`,
					placeholder: `New ${itemLabel}`,
					value: adding,
					onChange: (event) => setAdding(event.target.value)
				}), /* @__PURE__ */ jsx(Button, {
					size: "sm",
					variant: "secondary",
					leadingIcon: "plus",
					type: "submit",
					disabled: !adding.trim(),
					children: "Add"
				})]
			}),
			/* @__PURE__ */ jsx("span", {
				className: "ds-settings-section__status",
				role: "status",
				children: status
			}),
			/* @__PURE__ */ jsx(Modal, {
				open: deleting !== null,
				onClose: () => setDeleting(null),
				title: `Delete ${deleting?.name ?? ""}?`,
				size: "sm",
				footer: /* @__PURE__ */ jsxs(Fragment$1, { children: [/* @__PURE__ */ jsx(Button, {
					variant: "secondary",
					onClick: () => setDeleting(null),
					children: "Cancel"
				}), /* @__PURE__ */ jsxs(Button, {
					variant: "danger",
					onClick: () => void confirmDelete(),
					children: ["Delete ", itemLabel]
				})] }),
				children: deleting?.usage ? /* @__PURE__ */ jsxs(Alert, {
					tone: "warning",
					title: `Used by ${deleting.usage} ${usageWord(deleting.usage)}`,
					children: [
						capitalised,
						" ",
						deleting.name,
						" is removed from them too. This can’t be undone."
					]
				}) : /* @__PURE__ */ jsx("p", {
					className: "ds-settings-list__confirm",
					children: "This can’t be undone."
				})
			})
		]
	});
}
//#endregion
export { SettingsList, SettingsSection };

//# sourceMappingURL=SettingsSection.js.map